import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, money, apiErr } from "@/lib/api";
import { Table, Input, Button, Badge } from "@/lib/ui";

const SELLER_TYPES = ["manufacturer", "wholesaler", "retailer"];
const cap = (s) => (s || "").charAt(0).toUpperCase() + (s || "").slice(1);

const blank = (kind) => ({
  name: "", features: "", amount: "", num_products: "",
  seller_types: kind === "subscription" ? ["wholesaler", "retailer"] : ["manufacturer", "wholesaler", "retailer"],
  status: "active", kind, one_time: false,
});

export default function PlansAdmin() {
  const [tab, setTab] = useState("subscription");
  const [packages, setPackages] = useState([]);
  const [modal, setModal] = useState(null); // { kind, editId }
  const [form, setForm] = useState(blank("subscription"));
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/packages").then(({ data }) => setPackages(data));
  useEffect(() => { load(); }, []);

  const subPkgs = packages.filter((p) => (p.kind || "subscription") === "subscription");
  const addPkgs = packages.filter((p) => p.kind === "additional");
  const crmPkgs = packages.filter((p) => p.kind === "crm");
  const rows = tab === "subscription" ? subPkgs : tab === "additional" ? addPkgs : crmPkgs;

  const openAdd = () => { setForm(blank(tab)); setModal({ kind: tab, editId: null }); };
  const openEdit = (p) => {
    setForm({
      name: p.name || "", features: p.features || "", amount: p.amount ?? "",
      num_products: p.num_products ?? p.product_slots ?? "",
      seller_types: p.seller_types && p.seller_types.length ? p.seller_types
        : (p.visible_to && p.visible_to !== "both" ? [p.visible_to] : ["wholesaler", "retailer"]),
      status: p.status || "active", kind: p.kind || "subscription", one_time: !!p.one_time,
    });
    setModal({ kind: p.kind || "subscription", editId: p.id });
  };

  const toggleType = (t) => setForm((f) => ({
    ...f, seller_types: f.seller_types.includes(t) ? f.seller_types.filter((x) => x !== t) : [...f.seller_types, t],
  }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Package name required"); return; }
    if (form.seller_types.length === 0) { toast.error("Select at least one seller type"); return; }
    const products = Number(form.num_products) || 0;
    const payload = {
      name: form.name.trim(), kind: form.kind, features: form.features.trim(),
      amount: Number(form.amount) || 0, num_products: products, product_slots: products,
      seller_types: form.seller_types,
      visible_to: form.seller_types.length === 3 ? "both" : form.seller_types[0],
      validity: "Annual", gst: "Excluded", status: form.status, one_time: form.kind === "subscription" ? !!form.one_time : false,
    };
    setBusy(true);
    try {
      if (modal.editId) { await api.put(`/admin/packages/${modal.editId}`, payload); toast.success("Package updated"); }
      else { await api.post("/admin/packages", payload); toast.success("Package created"); }
      setModal(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    await api.delete(`/admin/packages/${id}`); toast.success("Deleted"); load();
  };

  const visibleTo = (p) => {
    const st = p.seller_types && p.seller_types.length ? p.seller_types
      : (p.visible_to && p.visible_to !== "both" ? [p.visible_to] : SELLER_TYPES);
    return st.map(cap).join(", ");
  };

  const kindLabel = modal?.kind === "additional" ? "Additional" : modal?.kind === "crm" ? "Leads CRM" : "Subscription";

  return (
    <div>
      <div className="flex items-start gap-3">
        <i className="fa-solid fa-layer-group mt-1 text-2xl text-orange-600" />
        <div>
          <h1 className="font-head text-2xl font-extrabold text-slate-900">Package Planner</h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            Create yearly subscription packages. Each package sets how many product listings a seller gets. All prices are + GST, valid 1 year.
          </p>
        </div>
      </div>

      <div className="mt-5">
        <Button onClick={openAdd} data-testid="add-package-btn"><i className="fa-solid fa-plus" /> Add Package</Button>
      </div>

      <div className="mt-5 flex gap-6 border-b border-slate-200 text-sm font-semibold">
        {[["subscription", "Subscription Packages", subPkgs.length, "fa-layer-group"],
          ["additional", "Additional Packages", addPkgs.length, "fa-cubes-stacked"],
          ["crm", "Leads CRM", crmPkgs.length, "fa-headset"]].map(([k, t, n, ic]) => (
          <button key={k} data-testid={`plans-tab-${k}`} onClick={() => setTab(k)}
            className={`-mb-px flex items-center gap-2 border-b-2 px-1 pb-3 transition ${tab === k ? "border-orange-600 text-orange-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <i className={`fa-solid ${ic}`} />{t}
            <span className={`rounded-full px-2 py-0.5 text-xs ${tab === k ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-500"}`}>{n}</span>
          </button>
        ))}
      </div>

      <div className="mt-5">
        <Table head={["Name", "Amount (₹)", "Products", "Features", "Visible To", "Status", "Actions"]}>
          {rows.map((p) => (
            <tr key={p.id} data-testid={`pkg-row-${p.id}`}>
              <td className="px-4 py-4 font-head font-extrabold uppercase text-orange-600">{p.name}</td>
              <td className="px-4 py-4 whitespace-nowrap font-bold text-slate-900">{money(p.amount || 0)} <span className="text-xs font-medium text-slate-400">+ GST</span></td>
              <td className="px-4 py-4 whitespace-nowrap font-semibold text-slate-700">{p.num_products ?? p.product_slots} products</td>
              <td className="px-4 py-4 max-w-xs text-xs uppercase text-slate-500">{p.features || "—"}</td>
              <td className="px-4 py-4 whitespace-nowrap text-slate-600">{visibleTo(p)}</td>
              <td className="px-4 py-4"><Badge tone={p.status === "active" ? "emeraldSoft" : "slate"}>{p.status === "active" ? "Active" : "Inactive"}</Badge>{p.one_time && <Badge tone="amber" className="ml-1">One-time</Badge>}</td>
              <td className="px-4 py-4">
                <div className="flex gap-2">
                  <button onClick={() => openEdit(p)} data-testid={`pkg-edit-${p.id}`} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"><i className="fa-solid fa-pen" /></button>
                  <button onClick={() => del(p.id)} data-testid={`pkg-del-${p.id}`} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-red-500 hover:bg-red-50"><i className="fa-solid fa-trash" /></button>
                </div>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No {tab} packages yet</td></tr>}
        </Table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setModal(null)}>
          <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 ds-scroll" onClick={(e) => e.stopPropagation()} data-testid="package-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-head text-xl font-extrabold text-slate-900">{modal.editId ? "Edit" : "Add"} {kindLabel} Package</h2>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-700" data-testid="package-modal-close"><i className="fa-solid fa-xmark text-lg" /></button>
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Package Name</label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Premium" data-testid="pkg-name" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Features / Description</label>
                <Input value={form.features} onChange={(e) => setForm((f) => ({ ...f, features: e.target.value }))} placeholder="e.g. Listing + Instagram promotions" data-testid="pkg-features" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Amount (₹, + GST)</label>
                <Input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="e.g. 2500" data-testid="pkg-amount" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Number of Products</label>
                <Input type="number" value={form.num_products} onChange={(e) => setForm((f) => ({ ...f, num_products: e.target.value }))} placeholder="e.g. 5" data-testid="pkg-numproducts" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Valid for Seller Types</label>
                <div className="flex flex-wrap gap-2">
                  {SELLER_TYPES.map((t) => {
                    const on = form.seller_types.includes(t);
                    return (
                      <button key={t} type="button" onClick={() => toggleType(t)} data-testid={`pkg-type-${t}`}
                        className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition ${on ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 bg-white text-slate-500"}`}>
                        <i className={`fa-solid ${on ? "fa-circle-check" : "fa-circle"}`} />{cap(t)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                <span className="text-sm font-semibold text-slate-700">Active (visible to sellers)</span>
                <button type="button" onClick={() => setForm((f) => ({ ...f, status: f.status === "active" ? "inactive" : "active" }))}
                  data-testid="pkg-active-toggle"
                  className={`relative h-6 w-11 rounded-full transition ${form.status === "active" ? "bg-orange-500" : "bg-slate-300"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${form.status === "active" ? "left-[22px]" : "left-0.5"}`} />
                </button>
              </div>
              {form.kind === "subscription" && (
                <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                  <span className="text-sm font-semibold text-slate-700">One-time starter package<br /><span className="text-xs font-normal text-slate-500">Can be bought only once per seller — never on renewal or as an add-on</span></span>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, one_time: !f.one_time }))} data-testid="pkg-onetime-toggle"
                    className={`relative h-6 w-11 rounded-full transition ${form.one_time ? "bg-orange-500" : "bg-slate-300"}`}>
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${form.one_time ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </div>
              )}
              <Button className="w-full py-3" onClick={save} disabled={busy} data-testid="pkg-save">
                {busy ? "Saving…" : "Save Package"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
